import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

/**
 * Maxus catalogue overlay (Neil 8 Oct spec, A2). Native drill-down:
 * van model -> category -> leaf -> exploded fisheye (zoomable) + callouts + products
 * (with description) + compatible vans. "Use this part" calls onSelect(product).
 * Data via the app proxy: /api/maxus-catalogue/{models,children,leaf}.
 */
export default function MaxusCatalogueOverlay({ open, onClose, onSelect, vin }) {
  const [stack, setStack] = useState([]);      // breadcrumb: [{term, name}], term null = models root
  const [items, setItems] = useState([]);       // current level's models/categories
  const [leaf, setLeaf] = useState(null);       // leaf detail when viewing a diagram
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [zoom, setZoom] = useState(false);
  const [showAllConfirm, setShowAllConfirm] = useState(false);

  const loadModels = useCallback(() => {
    setLoading(true); setError(""); setLeaf(null);
    axios.get("/api/maxus-catalogue/models")
      .then((r) => {
        const models = (r.data || []).map((m) => ({ ...m, has_diagram: false, child_count: 1 }));
        setItems(models); setStack([{ term: null, name: "Choose a van" }]);
      })
      .catch(() => setError("Could not load the Maxus catalogue."))
      .finally(() => setLoading(false));
  }, []);

  const loadChildren = (term, name, newStack) => {
    setLoading(true); setError(""); setLeaf(null);
    axios.get("/api/maxus-catalogue/children", { params: { term } })
      .then((r) => { setItems(r.data || []); setStack(newStack); })
      .catch(() => setError("Could not load that category."))
      .finally(() => setLoading(false));
  };

  const loadLeaf = (term, name) => {
    setLoading(true); setError(""); setZoom(false);
    axios.get("/api/maxus-catalogue/leaf", { params: { term } })
      .then((r) => setLeaf({ ...r.data, _name: name }))
      .catch(() => setError("Could not load that diagram."))
      .finally(() => setLoading(false));
  };

  // On open: if we know the van's VIN, resolve its model and go straight to its categories;
  // otherwise show the full van list.
  useEffect(() => {
    if (!open) return;
    setFilter(""); setLeaf(null); setError(""); setShowAllConfirm(false);
    if (vin) {
      setLoading(true);
      axios.get("/api/maxus-catalogue/model-for-vin", { params: { vin } })
        .then((r) => {
          const t = r.data && r.data.term_id;
          if (t) { loadChildren(t, r.data.name, [{ term: t, name: r.data.name }]); }
          else { loadModels(); }
        })
        .catch(() => loadModels())
        .finally(() => setLoading(false));
    } else { loadModels(); }
  }, [open, vin]);

  const openItem = (it) => {
    if (it.has_diagram) { loadLeaf(it.term_id, it.name); }
    else if (it.child_count > 0) { loadChildren(it.term_id, it.name, [...stack, { term: it.term_id, name: it.name }]); }
    else { loadLeaf(it.term_id, it.name); }
  };

  const goTo = (idx) => {
    const target = stack[idx];
    const newStack = stack.slice(0, idx + 1);
    if (!target || target.term == null) { loadModels(); }
    else { loadChildren(target.term, target.name, newStack); }
  };

  if (!open) return null;

  const atModels = stack.length <= 1;
  const shown = atModels && filter
    ? items.filter((m) => (m.name || "").toLowerCase().includes(filter.toLowerCase()))
    : items;

  const overlayBg = { position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 1000,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
  const panel = { background: "#fff", borderRadius: 10, width: "min(960px,96vw)", maxHeight: "92vh",
    overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 10px 40px rgba(0,0,0,.3)" };
  const head = { display: "flex", alignItems: "center", gap: 10, padding: "12px 16px",
    borderBottom: "1px solid #eee", background: "#BF3617", color: "#fff" };
  const body = { padding: 16, overflowY: "auto" };

  return (
    <div style={overlayBg} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={head}>
          <strong style={{ flex: 1 }}>Maxus parts catalogue</strong>
          <button className="button is-small" onClick={onClose}>Close ✕</button>
        </div>

        {/* breadcrumb */}
        <div style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0", fontSize: 13 }}>
          {stack.map((s, i) => (
            <span key={i}>
              {i > 0 && <span style={{ color: "#aaa" }}> › </span>}
              <a onClick={() => goTo(i)} style={{ cursor: "pointer", color: "#BF3617" }}>{s.name}</a>
            </span>
          ))}
          {leaf && <span><span style={{ color: "#aaa" }}> › </span>{leaf._name || leaf.name}</span>}
          {stack[0] && stack[0].term != null && (
            <button className="button is-small" style={{ float: "right" }}
              onClick={() => setShowAllConfirm(true)}>All vans ▾</button>
          )}
        </div>

        <div style={body}>
          {error && <p className="notification is-danger is-light">{error}</p>}
          {loading && <p>Loading…</p>}

          {/* LEAF DETAIL */}
          {!loading && leaf && (
            <div>
              <button className="button is-small" style={{ marginBottom: 10 }}
                onClick={() => { setLeaf(null); }}>‹ Back to list</button>
              {leaf.svg_url && (
                <div style={{ textAlign: "center", border: "1px solid #eee", borderRadius: 8, padding: 8,
                              marginBottom: 12, cursor: "zoom-in", background: "#fafafa" }}
                     onClick={() => setZoom(true)} title="Click to enlarge">
                  <img src={leaf.svg_url} alt="Exploded diagram"
                       style={{ maxWidth: "100%", maxHeight: 360, objectFit: "contain" }} />
                  <div style={{ fontSize: 12, color: "#888" }}>Click the diagram to enlarge</div>
                </div>
              )}
              {leaf.compatible_vans && leaf.compatible_vans.length > 0 && (
                <p style={{ fontSize: 13, marginBottom: 10 }}>
                  <strong>Fits:</strong> {leaf.compatible_vans.join(", ")}
                </p>
              )}
              <table className="table is-fullwidth is-narrow is-hoverable">
                <thead><tr><th></th><th>Part</th><th>Price</th><th></th></tr></thead>
                <tbody>
                  {(leaf.products || []).map((p) => (
                    <tr key={p.sku}>
                      <td style={{ width: 44 }}>{p.image ? <img src={p.image} alt="" style={{ width: 36 }} /> : null}</td>
                      <td>
                        <strong>{p.sku}</strong> — {p.name}
                        {p.short_desc ? <div style={{ fontSize: 12, color: "#777" }}>{p.short_desc}</div> : null}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>£{Number(p.price_net || 0).toFixed(2)}<br />
                        <span style={{ fontSize: 11, color: "#999" }}>£{Number(p.price_inc || 0).toFixed(2)} inc</span></td>
                      <td><button className="button is-small is-link"
                        onClick={() => { onSelect && onSelect({ name: p.name, part_number: p.sku, price: p.price_net }); }}>
                        Use this part</button></td>
                    </tr>
                  ))}
                  {(!leaf.products || leaf.products.length === 0) && (
                    <tr><td colSpan={4} style={{ color: "#999" }}>No parts listed for this diagram.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* BROWSE LIST */}
          {!loading && !leaf && (
            <div>
              {atModels && (
                <div style={{ display: "flex", gap: 6, marginBottom: 12, maxWidth: 360 }}>
                  <input className="input" placeholder="Filter vans…" value={filter}
                    onChange={(e) => setFilter(e.target.value)} />
                  {filter && (
                    <button className="button" title="Clear" onClick={() => setFilter("")}>✕</button>
                  )}
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(230px,1fr))", gap: 8 }}>
                {shown.map((it) => (
                  <button key={it.term_id} className="button is-fullwidth" style={{ justifyContent: "space-between" }}
                    onClick={() => openItem(it)}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{it.name}</span>
                    <span style={{ color: "#aaa" }}>{it.has_diagram ? "◧" : "›"}</span>
                  </button>
                ))}
                {shown.length === 0 && <p style={{ color: "#999" }}>Nothing here.</p>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* All-vans compatibility warning */}
      {showAllConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 1200,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => setShowAllConfirm(false)}>
          <div style={{ background: "#fff", borderRadius: 8, padding: 20, maxWidth: 440 }}
            onClick={(e) => e.stopPropagation()}>
            <p style={{ marginBottom: 14 }}>
              By choosing a different van you will see other models, and the parts might
              <strong> not be compatible with this vehicle</strong>. Do you want to continue?
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="button is-small" onClick={() => setShowAllConfirm(false)}>No</button>
              <button className="button is-small is-danger"
                onClick={() => { setShowAllConfirm(false); loadModels(); }}>Yes, show all vans</button>
            </div>
          </div>
        </div>
      )}

      {/* ZOOM lightbox */}
      {zoom && leaf && leaf.svg_url && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.85)", zIndex: 1100,
          display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out", overflow: "auto" }}
          onClick={() => setZoom(false)}>
          <button className="button" style={{ position: "fixed", top: 16, right: 16, zIndex: 1101 }}
            onClick={(e) => { e.stopPropagation(); setZoom(false); }}>Close ✕</button>
          <img src={leaf.svg_url} alt="Exploded diagram (enlarged)"
            style={{ width: "140%", maxWidth: "none", margin: "auto" }}
            onClick={(e) => { e.stopPropagation(); setZoom(false); }} />
        </div>
      )}
    </div>
  );
}
